import React, { useEffect, useState } from "react";
import { useDataWithRoute, useTitleFromComponent } from "../../../../utils/hooks";
import { useSearchParams } from "react-router-dom";
import { VsiBubble, VsiH4 } from "../../../../styled/App.styled";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import { Card, Col, Row, Button } from "reactstrap";
import Stockchart from "../../../../components/Stockchart";
import Grid from "../../../../components/Grid";
import {
  capitalizeWord,
  formatDualAxisStockChartConfig,
  toSnakeCase
} from "../../../../utils/Helpers";

import {
  allAllocatedTrendDefs,
  allCapacityAllPoolsTrendDefs,
  allCapacityByShareTrendDefs,
  allCapacityByVolumeTrendDefs,
  allCapacityDdrTrendDefs,
  allCapacityTrendDefs,
  allCpuTrendDefs,
  allIopsTrendDefs,
  allMbsecTrendDefs,
  allNodeTrendDefs,
  allPhysUsedTrendDefs,
  allUsedTrendDefs,
  collectionDateDef,
  nodeCapacityTrendDefs,
  nodeLatencyTrendDefs,
  NodePhysUsedTrendDefs,
  NodeUsedTrendDefs,
  otherAllocatedTrendDefs,
  otherIopsTrendDefs,
  otherLatencyTrendDefs,
  poolCapacityTrendDefs,
  poolLatencyTrendDefs,
  PoolPhysUsedTrendDefs,
  PoolUsedTrendDefs,
  ShareUsedTrendDefs,
  volumeIopsTrendDefs,
  volumeLatencyTrendDefs,
  VolumePhysUsedTrendDefs,
  VolumeUsedTrendDefs
} from "../../../../utils/gridColumnDefs/storage/device/deviceTrends";

import { useVsiAppContext } from "../../../../stores/VsiApp";
import {
  enterpriseTrendColors,
  genericColors,
  iopsLineColor,
  latencyLineColor, latencyLineColorSwap,
  mbsecLineColor
} from "../../../../utils/chartUtils";

const chartCapacityMetrics = [
  { value: "all_history", label: "All History", route: "device_trends_all_history_proc" }, // Default
  { value: "all_nodes", label: "All Nodes", route: "device_trends_all_nodes_proc" },
  // {value: 'all_volumes', label: 'All Volumes', route: 'device_trends_all_volumes_proc'},
  { value: "all_pools", label: "All Pools", route: "device_trends_all_pools_proc" },
  { value: "by_node", label: "By Node", route: "device_trends_by_node_proc" },
  { value: "by_volume", label: "By Volume", route: "device_trends_by_volume_proc" },
  { value: "by_share", label: "By Share", route: "device_trends_by_share_proc" },
  { value: "by_pool", label: "By Pool", route: "device_trends_by_pool_proc" }
];

const allMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "total_free", label: "Free" },
  { value: "total_allocated", label: "Allocated" },
  { value: "usable", label: "Capacity" },
  { value: "iops", label: "IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" },
  { value: "mbsec", label: "MBSec" }
  // { value: 'drr', label: 'Data Reduction Ratio' },
];

const allPoolsMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "total_free", label: "Free" },
  { value: "allocated", label: "Allocated" },
  { value: "capacity", label: "Capacity" },
  { value: "iops", label: "IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" },
  { value: "mbsec", label: "MBSec" }
  // { value: 'drr', label: 'Data Reduction Ratio' },
];

const historyMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "phys_used", label: "Physical Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "read_iops", label: "Read IOPs" },
  { value: "write_iops", label: "Write IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" },
  { value: "mbsec", label: "MBSec" },
  { value: "drr", label: "Data Reduction Ratio" }
];

const nodeMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" },
  { value: "mbsec", label: "MBSec" }
];

const poolsMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" },
  { value: "mbsec", label: "MBSec" }
];

const volMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "read_latency", label: "Read Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "avg_latency", label: "Avg Latency" }
];

const shareMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "allocated", label: "Allocated" }
];

export default function DeviceHistoricalTrends(props) {
  const { theme, client, periodId, user } = useVsiAppContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const metric = searchParams.get("metric") || "capacity";
  let defaultMetric = chartCapacityMetrics.find(({ value }) => metric === value) || chartCapacityMetrics[0];
  const [chartMetric, setChartMetric] = useState(defaultMetric);
  const [activeMetricSelection, setActiveMetricSelection] = useState(chartCapacityMetrics);

  const [chartData, setChartData] = useState([]);
  const [chartDefs, setChartDefs] = useState([]);
  const [formattedChartData, setFormattedChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({});

  const [additionalChartOption, setAdditionalChartOption] = useState({});
  const [additionalChartOptionSelection, setAdditionalChartOptionSelection] = useState([]);
  const [additionalChartOptionLabel, setAdditionalChartOptionLabel] = useState(capitalizeWord(searchParams.get("metric")?.split("_")[1]) || "");
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const [axis1Type, setAxis1Type] = useState(allMetricTypes[1]);
  const [axis2Type, setAxis2Type] = useState(allMetricTypes[0]);

  const [isMunging, setIsMunging] = useState(false);

  const dataTime = searchParams.get("data_time") || "historical"; // historical or interval
  const deviceName = searchParams.get("device_name");

  useTitleFromComponent(`${deviceName} / ${props.title}`);

  const getRouteForSelection = route => {
    let routeToSearch = `/api/v6/report/${encodeURI(route)}?client_id=${client.clientId}&device_name=${encodeURI(deviceName)}&period_id=${periodId}`;
    if (additionalChartOptionLabel.toLowerCase() === "pool" && searchParams.get("pool")) {
      routeToSearch += `&pool_name=${encodeURI(searchParams.get("pool"))}`;
    }
    if (additionalChartOptionLabel.toLowerCase() === "node" && searchParams.get("node")) {
      routeToSearch += `&node_name=${encodeURI(searchParams.get("node"))}`;
    }
    if (additionalChartOptionLabel.toLowerCase() === "volume" && searchParams.get("volume")) {
      routeToSearch += `&volume_name=${encodeURIComponent(searchParams.get("volume"))}`;
    }
    if (additionalChartOptionLabel.toLowerCase() === "share" && searchParams.get("share")) {
      routeToSearch += `&share_name=${encodeURI(searchParams.get("share"))}`;
    }
    return routeToSearch;
  };

  const [data, { isLoading }] = useDataWithRoute(getRouteForSelection(chartMetric.route), dataTime === "historical");

  const [record, options] = data?.recordsets;

  useEffect(() => {
    if (options?.[0]) {
      const keyToUse = Object.keys(options[0])[0];
      const allOptions = options.map(opt => ({
        label: opt[keyToUse],
        value: opt[keyToUse]
      }));
      setAdditionalChartOptionSelection(allOptions);
      setAdditionalChartOptionLabel(capitalizeWord(keyToUse.replace("_name", "")));
    }
  }, [options]);

  useEffect(() => {
    if (additionalChartOptionSelection[0] && !additionalChartOption?.hasOwnProperty?.("value")) {
      setAdditionalChartOption(additionalChartOptionSelection[0]);
      searchParams.set(additionalChartOptionLabel.toLowerCase(), additionalChartOptionSelection[0].value);
      setSearchParams(searchParams);
    }
  }, [additionalChartOptionSelection]);

  useEffect(() => {
    let metric = searchParams.get("metric")?.split("_")[1];
    let defaultChoice = searchParams.get("node") || searchParams.get("volume") || searchParams.get("pool");
    if (defaultChoice && defaultChoice !== "undefined") {
      let defaultConfigured = {
        value: defaultChoice,
        label: defaultChoice,
        route: `${metric}_name=${defaultChoice}`
      };
      setAdditionalChartOption(defaultConfigured);
    }

    let metrictype;
    if (metric === "node") {
      metrictype = nodeMetricTypes;
      setDropdownOptions(nodeMetricTypes);
    } else if (metric === "volume") {
      metrictype = volMetricTypes;
      setDropdownOptions(volMetricTypes);
    } else if (metric === "share") {
      metrictype = shareMetricTypes;
      setDropdownOptions(shareMetricTypes);
    } else if (metric === "pool") {
      metrictype = poolsMetricTypes;
      setDropdownOptions(poolsMetricTypes);
    } else if (metric === "history") {
      metrictype = historyMetricTypes;
      setDropdownOptions(historyMetricTypes);
    } else if (metric === "pools") {
      metrictype = allPoolsMetricTypes;
      setDropdownOptions(allPoolsMetricTypes);
    } else {
      metrictype = allMetricTypes;
      setDropdownOptions(allMetricTypes);
    }
    let axis1 = searchParams.get("axis_1") || metrictype[1].value;
    let axis2 = searchParams.get("axis_2") || metrictype[0].value;
    let defaultAxis1 = metrictype.find(({ value }) => axis1 === value) || metrictype[1];
    let defaultAxis2 = metrictype.find(({ value }) => axis2 === value) || metrictype[0];

    if (!axis1Type) {
      setAxis1Type(defaultAxis1);
      setAxis2Type(defaultAxis2);
    }

    let newChartMetric = chartCapacityMetrics.find(({ value }) => value === chartMetric?.value) || chartCapacityMetrics[0];
    setActiveMetricSelection(chartCapacityMetrics);
    if (searchParams.get("metric") !== null && chartMetric && data.recordsets?.[0]?.length && !isLoading) {
      setChartData(data?.recordsets[0] || []);
      setChartDefs(defineColumnDefs(chartMetric.value));
      handleStateWithData(data?.recordsets);
    }

    if (!searchParams.get("metric")) {
      onMetricChange(newChartMetric);
    }
  }, [record, options, axis1Type, axis2Type]);

  const setAllAdditionalParams = (selection, metric) => {
    searchParams.delete("node");
    searchParams.delete("pool");
    searchParams.delete("volume");
    searchParams.delete("share");

    if (metric !== "") {
      searchParams.set(metric.toLowerCase(), selection?.[0]?.value);
      setAdditionalChartOptionLabel(metric);
    } else {
      setAdditionalChartOptionLabel("");
    }
    setSearchParams(searchParams);
  };

  const defineColumnDefs = (metric) => {
    let defsArray = [];

    switch (metric) {
      case "all_history":
        defsArray.push(allCpuTrendDefs);
        break;
      case "all_nodes":
        defsArray.push(allCpuTrendDefs);
        defsArray.push(allNodeTrendDefs);
        break;
      // case 'all_volumes':
      //     defsArray.push(allCapacityByVolumeTrendDefs);
      //     break;
      case "all_pools":
        defsArray.push(allCapacityAllPoolsTrendDefs);
        break;
      case "by_node":
        defsArray.push(allNodeTrendDefs);
        break;
      case "by_volume":
        defsArray.push(allCapacityByVolumeTrendDefs);
        break;
      case "by_share":
        defsArray.push(allCapacityByShareTrendDefs);
        break;
      case "by_pool":
        defsArray.push(allCapacityAllPoolsTrendDefs);
        break;
      default:
        break;
    }

    let axes = [axis1Type, axis2Type];
    for (let i = 0; i <= 1; i++) {
      let paramMetric = searchParams.get("metric").split("_")[1];
      switch (axes[i].value) {
        case "used":
          if (paramMetric.includes("node")) {
            defsArray.push(NodeUsedTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(VolumeUsedTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(PoolUsedTrendDefs);
          } else if (paramMetric.includes("share")) {
            defsArray.push(ShareUsedTrendDefs);
          } else {
            defsArray.push(allUsedTrendDefs);
          }
          break;
        case "phys_used":
          if (paramMetric.includes("node")) {
            defsArray.push(NodePhysUsedTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(VolumePhysUsedTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(PoolPhysUsedTrendDefs);
          } else {
            defsArray.push(allPhysUsedTrendDefs);
          }
          break;
        case "iops":
          if (paramMetric.includes("history")) {
            defsArray.push(allIopsTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(volumeIopsTrendDefs);
          } else {
            defsArray.push(otherIopsTrendDefs);
          }
          break;
        case "latency":
          if (paramMetric.includes("node")) {
            defsArray.push(nodeLatencyTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(volumeLatencyTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(poolLatencyTrendDefs);
          } else {
            defsArray.push(otherLatencyTrendDefs);
          }
          break;
        case "avg_latency":
          if (paramMetric.includes("node")) {
            defsArray.push(nodeLatencyTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(volumeLatencyTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(poolLatencyTrendDefs);
          } else {
            defsArray.push(otherLatencyTrendDefs);
          }
          break;
        case "write_latency":
          if (paramMetric.includes("node")) {
            defsArray.push(nodeLatencyTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(volumeLatencyTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(poolLatencyTrendDefs);
          } else {
            defsArray.push(otherLatencyTrendDefs);
          }
          break;
        case "capacity":
          if (paramMetric.includes("node")) {
            defsArray.push(nodeCapacityTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(poolCapacityTrendDefs);
          } else {
            defsArray.push(allCapacityTrendDefs);
          }

          if (paramMetric.includes("node")) {
            defsArray.push(NodeUsedTrendDefs);
          } else if (paramMetric.includes("volume")) {
            defsArray.push(VolumeUsedTrendDefs);
          } else if (paramMetric.includes("pool")) {
            defsArray.push(PoolUsedTrendDefs);
          } else {
            defsArray.push(allUsedTrendDefs);
          }
          break;
        case "allocated":
          if (paramMetric.includes("volume") || paramMetric.includes("pool")) {
            defsArray.push(otherAllocatedTrendDefs);
          } else {
            defsArray.push(allAllocatedTrendDefs);
          }
          break;
        case "mbsec":
          defsArray.push(allMbsecTrendDefs);
          break;
        case "drr":
          defsArray.push(allCapacityDdrTrendDefs);
          break;
        default:
          break;
      }
    }

    return [...collectionDateDef].concat(...defsArray);
  };

  const defineChartConfigs = () => {
    let returnFields = [];
    var axes = [axis1Type, axis2Type];
    for (let i = 0; i <= 1; i++) {
      switch (axes[i].value) {
        case "used":
          let usedMetric = searchParams.get("metric").split("_")[1];
          if (usedMetric.includes("node")) {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (usedMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Free",
                field: "free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
              // {
              //     name: 'Capacity',
              //     field: 'usable_gb',
              //     yAxis: i,
              //     color: genericColors[0],
              //
              // }
            ]);
          } else if (usedMetric.includes("share")) {
            returnFields.push([
              {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }

            ]);
          } else if (usedMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "capacity",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "total_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "usable_gb",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Orphaned",
                field: "orphan_gb",
                yAxis: i,
                color: enterpriseTrendColors[4],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          ;
          break;
        case "phys_used":
          let physUsedMetric = searchParams.get("metric").split("_")[1];
          if (physUsedMetric.includes("node")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (physUsedMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (physUsedMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Orphaned",
                field: "orphan_gb",
                yAxis: i,
                color: enterpriseTrendColors[4],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          ;
          break;
        case "iops":
          let iopsMetric = searchParams.get("metric").split("_")[1];
          if (iopsMetric.includes("volume")) {
            returnFields.push([
              {
                name: "IOPs",
                field: "volume_iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "IOPs",
                field: "iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          break;
        case "read_iops":
          let readIopsMetric = searchParams.get("metric").split("_")[1];
          if (readIopsMetric.includes("volume")) {
            returnFields.push([
              {
                name: "IOPs",
                field: "volume_iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Read IOPs",
                field: "read_iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          break;
        case "write_iops":
          let writeIopsMetric = searchParams.get("metric").split("_")[1];
          if (writeIopsMetric.includes("volume")) {
            returnFields.push([
              {
                name: "IOPs",
                field: "volume_iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Write IOPs",
                field: "write_iops",
                yAxis: i,
                color: iopsLineColor,
                dashStyle: "ShortDashDotDot",
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          break;
        case "latency":
        case "avg_latency":
          let latMetric = searchParams.get("metric").split("_")[1];
          if (latMetric.includes("node")) {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "ShortDashDotDot"
              },
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          } else if (latMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Avg Latency",
                field: "latency", // This one is special
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "ShortDashDotDot"
              }
            ]);
          } else if (latMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              },
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "DashDot"
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              },
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          }
          ;
          break;
        case "read_latency":
          let readLatMetric = searchParams.get("metric").split("_")[1];
          if (readLatMetric.includes("node")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          } else if (readLatMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }
            ]);
          } else if (readLatMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "DashDot"
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          }
          ;
          break;
        case "write_latency":
          let writeLatMetric = searchParams.get("metric").split("_")[1];
          if (writeLatMetric.includes("node")) {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          } else if (writeLatMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              }
            ]);
          } else if (writeLatMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "DashDot"
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          }
          ;
          break;
        case "mbsec":
          returnFields.push([
            {
              name: "MB/Sec",
              field: "mbsec",
              yAxis: i,
              color: mbsecLineColor
            }
          ]);
          break;
        case "drr":
          let drrMetric = searchParams.get("metric").split("_")[1];
          if (drrMetric.includes("history")) {
            returnFields.push([
              {
                name: "Data Reduction Ratio",
                field: "data_reduction_ratio",
                yAxis: i,
                color: genericColors[6]
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Data Reduction Ratio",
                field: "drr",
                yAxis: i,
                color: genericColors[6]
              }
            ]);
          }
          break;
        default:
          break;
      }
      ;
    }
    ;
    // Detect repeat colors then replace them
    const colorAlreadyShown = new Set();
    let swapCount = { read_latency: 0, write_latency: 0, avg_latency: 0 };
    returnFields.map((innerRF) => {
      innerRF.map((rF) => {
        if (swapCount.hasOwnProperty(rF.field)) {
          if (colorAlreadyShown.has(rF.color)) {
            rF.color = latencyLineColorSwap[rF.field][swapCount[rF.field]];
          }
          colorAlreadyShown.add(rF.color);
          swapCount[rF.field] += 1;
        }
      });
    });
    return [].concat(...returnFields);
  };

  function checkByParamsUsage(s, n) {
    // check other `by_` in search params
    const filteredMetrics = chartCapacityMetrics
      .filter(item => item.value.startsWith("by_"))
      .filter(item => item.value !== chartMetric.value)
      .map(item => item.value.replace(/by_/g, ""));
    for (let i of filteredMetrics) {
      if (searchParams.get(i)) {
        return true;
      }
    }
    return false;
  }

  const handleStateWithData = data => {
    setIsMunging(true);
    switch (chartMetric.value) {
      case "all_history":
        setAdditionalChartOptionSelection([]);
        setAdditionalChartOption({});
        setAllAdditionalParams([], "");

        const historyChartConfig = defineChartConfigs();
        const allHistoryConfig = formatDualAxisStockChartConfig(data?.[0] || [], historyChartConfig);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ],
          colors: historyChartConfig.map(({ color }) => color)
        });

        setFormattedChartData(allHistoryConfig.series);
        break;
      case "all_pools":
        setAdditionalChartOptionSelection([]);
        setAdditionalChartOption({});
        setAllAdditionalParams([], "");
        let findPools = {};
        const poolOrganization = {};

        data?.[0]?.forEach(datum => {
          if (!findPools[datum.pool_name]) {
            findPools[datum.pool_name] = true;
          }

          if (!poolOrganization[datum.collection_date]) {
            poolOrganization[datum.collection_date] = {};
          }

          if (!poolOrganization[datum.collection_date][datum.pool_name]) {
            poolOrganization[datum.collection_date][datum.pool_name] = {
              [axis1Type.value]: 0,
              [axis2Type.value]: 0
            };
          }

          const axis1DrillDown = poolOrganization[datum.collection_date][datum.pool_name][axis1Type.value];
          const axis2DrillDown = poolOrganization[datum.collection_date][datum.pool_name][axis2Type.value];
          poolOrganization[datum.collection_date][datum.pool_name][axis1Type.value] = axis1DrillDown + datum[axis1Type.value];
          poolOrganization[datum.collection_date][datum.pool_name][axis2Type.value] = axis2DrillDown + datum[axis2Type.value];

          return datum;
        });

        const sortedPoolKeys = Object.keys(poolOrganization).sort((a, b) => Date.parse(a?.split?.("T")?.[0]) - Date.parse(b?.split?.("T")?.[0]));
        const allPools = Object.keys(findPools);

        const allPoolNamesForChart = [];

        const allPoolDataForBothAxes = sortedPoolKeys.map(colDate => {
          const dataToRet = {
            collection_date: colDate
          };
          const thisDateDataPoint = poolOrganization[colDate];

          allPools.forEach(pool => {
            const thisDatePoolDataPoint = thisDateDataPoint[pool];
            dataToRet[`${pool} (${axis1Type.label})`] = thisDatePoolDataPoint?.[axis1Type.value] ?? 0;
            dataToRet[`${pool} (${axis2Type.label})`] = thisDatePoolDataPoint?.[axis2Type.value] ?? 0;

            if (!allPoolNamesForChart.includes(`${pool} (${axis1Type.label})`) && axis1Type.value !== "none") {
              allPoolNamesForChart.push(`${pool} (${axis1Type.label})`);
            }
            if (!allPoolNamesForChart.includes(`${pool} (${axis2Type.label})`) && axis2Type.value !== "none") {
              allPoolNamesForChart.push(`${pool} (${axis2Type.label})`);
            }
          });

          return dataToRet;
        });

        const allPoolFields = allPoolNamesForChart.map(pool => ({
          name: pool,
          field: pool,
          yAxis: pool.includes(`(${axis1Type.label})`) ? 0 : 1
        }));

        const allPoolsConfig = formatDualAxisStockChartConfig(allPoolDataForBothAxes || [], allPoolFields);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ]
        });

        setFormattedChartData(allPoolsConfig.series);
        break;

      case "all_nodes":
        setAdditionalChartOptionSelection([]);
        setAdditionalChartOption({});
        setAllAdditionalParams([], "");
        let findNodes = {};
        const nodeOrganization = {};

        data?.[0]?.forEach(datum => {
          if (!findNodes[datum.node_name]) {
            findNodes[datum.node_name] = true;
          }

          if (!nodeOrganization[datum.collection_date]) {
            nodeOrganization[datum.collection_date] = {};
          }

          if (!nodeOrganization[datum.collection_date][datum.node_name]) {
            nodeOrganization[datum.collection_date][datum.node_name] = {
              [axis1Type.value]: 0,
              [axis2Type.value]: 0
            };
          }

          const axis1DrillDown = nodeOrganization[datum.collection_date][datum.node_name][axis1Type.value];
          const axis2DrillDown = nodeOrganization[datum.collection_date][datum.node_name][axis2Type.value];
          nodeOrganization[datum.collection_date][datum.node_name][axis1Type.value] = axis1DrillDown + datum[axis1Type.value];
          nodeOrganization[datum.collection_date][datum.node_name][axis2Type.value] = axis2DrillDown + datum[axis2Type.value];

          return datum;
        });

        const sortedKeys = Object.keys(nodeOrganization).sort((a, b) => Date.parse(a?.split?.("T")?.[0]) - Date.parse(b?.split?.("T")?.[0]));
        const allNodes = Object.keys(findNodes);

        const allNodeNamesForChart = [];

        const allDataForBothAxes = sortedKeys.map(colDate => {
          const dataToRet = {
            collection_date: colDate
          };
          const thisDateDataPoint = nodeOrganization[colDate];

          allNodes.forEach(node => {
            const thisDateNodeDataPoint = thisDateDataPoint[node];
            dataToRet[`${node} (${axis1Type.label})`] = thisDateNodeDataPoint?.[axis1Type.value] ?? 0;
            dataToRet[`${node} (${axis2Type.label})`] = thisDateNodeDataPoint?.[axis2Type.value] ?? 0;

            if (!allNodeNamesForChart.includes(`${node} (${axis1Type.label})`) && axis1Type.value !== "none") {
              allNodeNamesForChart.push(`${node} (${axis1Type.label})`);
            }
            if (!allNodeNamesForChart.includes(`${node} (${axis2Type.label})`) && axis2Type.value !== "none") {
              allNodeNamesForChart.push(`${node} (${axis2Type.label})`);
            }
          });

          return dataToRet;
        });

        const allNodeFields = allNodeNamesForChart.map(node => ({
          name: node,
          field: node,
          yAxis: node.includes(`(${axis1Type.label})`) ? 0 : 1
        }));

        const allNodesConfig = formatDualAxisStockChartConfig(allDataForBothAxes || [], allNodeFields);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ]
        });

        setFormattedChartData(allNodesConfig.series);
        break;
      case "by_node":
        if (checkByParamsUsage()) {
          setAllAdditionalParams([], "");
          setAdditionalChartOption({});
          setAdditionalChartOptionSelection([]);
        }

        const nodeSelections = data?.[1]?.map?.(datum => {
          return { value: datum.node_name, label: datum.node_name, route: `node_name=${datum.node_name}` };
        }) ?? [];

        const byNodeChartConfig = defineChartConfigs();
        const byNodeConfig = formatDualAxisStockChartConfig(data?.[0] || [], byNodeChartConfig);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ],
          colors: byNodeChartConfig.map(({ color }) => color)
        });

        setAdditionalChartOptionSelection(nodeSelections);
        if (Object.keys(additionalChartOption).length === 0) {
          setAdditionalChartOption(nodeSelections[0]);
        }
        ;
        setFormattedChartData(byNodeConfig.series);
        if (!searchParams.get("node")) {
          setAllAdditionalParams(nodeSelections, "Node");
        }
        ;
        break;
      case "by_volume":
        if (checkByParamsUsage()) {
          setAllAdditionalParams([], "");
          setAdditionalChartOption({});
          setAdditionalChartOptionSelection([]);
        }
        ;

        const volSelections = data?.[1]?.map?.(datum => {
          return {
            value: datum.volume_name,
            label: datum.volume_name,
            route: `volume_name=${datum.volume_name}`
          };
        }) ?? [];

        const byVolumeChartConfig = defineChartConfigs();
        const byVolConfig = formatDualAxisStockChartConfig(data?.[0] || [], byVolumeChartConfig);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ],
          colors: byVolumeChartConfig.map(({ color }) => color)
        });

        setAdditionalChartOptionSelection(volSelections);
        if (Object.keys(additionalChartOption).length === 0) {
          setAdditionalChartOption(volSelections[0]);
        }

        setFormattedChartData(byVolConfig.series);
        if (!searchParams.get("volume")) {
          setAllAdditionalParams(volSelections, "Volume");
        }
        break;
      case "by_share":
        if (checkByParamsUsage()) {
          setAllAdditionalParams([], "");
          setAdditionalChartOption({});
          setAdditionalChartOptionSelection([]);
        }

        const shareSelections = data?.[1]?.map?.(datum => {
          return {
            value: datum.share_name,
            label: datum.share_name,
            route: `share_name=${datum.share_name}`
          };
        }) ?? [];

        const byShareChartConfig = defineChartConfigs();
        const byShareConfig = formatDualAxisStockChartConfig(data?.[0] || [], byShareChartConfig);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ],
          colors: byShareChartConfig.map(({ color }) => color)
        });

        setAdditionalChartOptionSelection(shareSelections);
        if (Object.keys(additionalChartOption).length === 0) {
          if (searchParams.has("navi_share")) {
            const navi_share = searchParams.get("navi_share");
            searchParams.delete("navi_share");
            setAdditionalChartOption({
              label: navi_share,
              route: `share_name=${navi_share}`,
              value: navi_share
            });
          } else {
            setAdditionalChartOption(shareSelections[0]);
          }
        }

        setFormattedChartData(byShareConfig.series);
        if (!searchParams.get("share")) {
          setAllAdditionalParams(shareSelections, "Share");
        }
        break;
      case "by_pool":
        if (checkByParamsUsage()) {
          setAllAdditionalParams([], "");
          setAdditionalChartOption({});
          setAdditionalChartOptionSelection([]);
        }

        const poolSelections = data?.[1]?.map?.(datum => {
          return { value: datum.pool_name, label: datum.pool_name, route: `pool_name=${datum.pool_name}` };
        }) ?? [];

        const byPoolChartConfig = defineChartConfigs();
        const byPoolConfig = formatDualAxisStockChartConfig(data?.[0] || [], byPoolChartConfig);

        setChartConfig({
          yAxis: [
            {
              title: {
                text: axis1Type.label
              }
            },
            {
              title: {
                text: axis2Type.label
              },
              opposite: true
            }
          ],
          colors: byPoolChartConfig.map(({ color }) => color)
        });

        const poolInQuery = searchParams.get("pool");
        setAdditionalChartOptionSelection(poolSelections);
        if (Object.keys(additionalChartOption).length === 0) {
          const selectedPool = poolSelections.find(pool => pool.value === decodeURIComponent(poolInQuery)) ?? poolSelections[0];
          setAdditionalChartOption(selectedPool);
          setAllAdditionalParams(poolSelections, "Pool");
        }
        setFormattedChartData(byPoolConfig.series);
        if (!poolInQuery) {
          setAllAdditionalParams(poolSelections, "Pool");
        }
        break;
      default:
      // do nothing
    }
    setIsMunging(false);
  };

  const onMetricChange = async (metric) => {
    setAdditionalChartOptionLabel("");
    setChartMetric(metric);
    // Reset single options, if there are any
    setAdditionalChartOption({});
    searchParams.delete("node");
    searchParams.delete("pool");
    searchParams.delete("volume");
    searchParams.delete("share");
    searchParams.set("metric", metric.value);
    // Reset yAxis dropdowns
    if (metric.value === "all_pools") {
      setAxis1Type(allPoolsMetricTypes[1]);
      setAxis2Type(allPoolsMetricTypes[0]);
    } else {
      setAxis1Type(allMetricTypes[1]);
      setAxis2Type(allMetricTypes[0]);
    }

    setSearchParams(searchParams, { replace: true });
  };

  const onAdditionalOptionChange = async (option) => {
    setAdditionalChartOption(option);
    searchParams.set(toSnakeCase(additionalChartOptionLabel), encodeURI(option.value));
    setSearchParams(searchParams, { replace: true });
  };

  const onAxis1Change = (axis) => {
    setAxis1Type(axis);
    searchParams.set("axis_1", axis.value);
    setSearchParams(searchParams, { replace: true });
  };

  const onAxis2Change = (axis) => {
    setAxis2Type(axis);
    searchParams.set("axis_2", axis.value);
    setSearchParams(searchParams, { replace: true });
  };

  const filterChanged = (eventData) => {
    let filteredData = [];
    eventData.api.forEachNodeAfterFilter(node => {
      filteredData.push(node.data);
    });

    handleStateWithData([filteredData]);
  };

  const onDataBtnClick = () => {
    searchParams.set("data_time", "interval");
    setSearchParams(searchParams, { replace: true });
  };

  const showHistBtn = user.role.includes("super_user");

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Device Trends - {deviceName}</VsiH4>
      <VsiBubble noAutobot>
        <div className={"w-100 p-2 d-flex flex-row justify-content-between"}>
          <div className={"d-flex"}>
            <VsiLabeledSelect options={activeMetricSelection}
                              isLoading={isLoading}
                              isDisabled={isLoading}
                              label={"Metric"}
                              minWidth={200}
                              maxWidth={500}
                              onChange={onMetricChange}
                              defaultValue={chartMetric} />
            {additionalChartOptionLabel && additionalChartOptionLabel?.toLowerCase?.() !== 'history' && (
              <VsiLabeledSelect options={additionalChartOptionSelection}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                                label={additionalChartOptionLabel}
                                minWidth={additionalChartOption?.label?.length > 15 ? 350 : 200}
                                maxWidth={600}
                                onChange={onAdditionalOptionChange}
                                defaultValue={additionalChartOption} />
            )}
            {showHistBtn ? (
              <div className={"d-flex flex-row align-items-end"}>
                <Button onClick={onDataBtnClick}>
                  Interval View
                  <i className={"fa-solid fa-timer ms-1"} />
                </Button>
              </div>
            ) : null}
          </div>

          <div className={"d-flex"}>
            <VsiLabeledSelect options={dropdownOptions.filter(({ value }) => value !== axis2Type.value)}
                              isLoading={isLoading}
                              isDisabled={isLoading}
                              label={"Left Axis"}
                              minWidth={200}
                              maxWidth={225}
                              onChange={onAxis1Change}
                              defaultValue={axis1Type} />
            <VsiLabeledSelect options={dropdownOptions.filter(({ value }) => value !== axis1Type.value)}
                              isLoading={isLoading}
                              isDisabled={isLoading}
                              label={"Right Axis"}
                              minWidth={200}
                              maxWidth={225}
                              onChange={onAxis2Change}
                              defaultValue={axis2Type} />
          </div>
        </div>
      </VsiBubble>
      <Row>
        <Col xs={12}>
          <VsiBubble unit={chartMetric.value} autobotInfo={{
            secondaryMetric: [additionalChartOption?.value],
            yAxes: axis2Type ? [axis1Type, axis2Type] : [axis1Type]
          }}>
            <Stockchart series={formattedChartData}
                        saveParams={{ deviceName: deviceName }}
                        isLoading={isLoading || isMunging}
                        config={chartConfig} />
          </VsiBubble>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <VsiBubble unit={chartMetric.value} autobotInfo={{ secondaryMetric: [additionalChartOption?.value] }}>
            <Grid addStyle
                  data={chartData}
                  saveParams={{ metric: chartMetric.value }}
                  columnDefs={chartDefs}
                  filterChanged={filterChanged}
            />
          </VsiBubble>
        </Col>
      </Row>
    </Card>
  );
}
